import React, { useContext, createContext } from 'react';
import PropTypes from 'prop-types';
import { Query } from 'react-apollo';
import { getClientEnvironmentDetails } from '@sm/utils';
import experimentsQuery from './Experiments.graphql';

/**
 * Initial context for hook
 * @type {React.Context<*[{Object}, {Function}]>}
 */
export const expContext = createContext({ experiments: [], loading: true });

/**
 * Runs the query to Experiments and provides back all experiments for the user
 * @param {Object} props
 * @returns {*}
 */
const ExperimentProvider = props => {
  const { isBrowser } = getClientEnvironmentDetails();
  const pageReferer = isBrowser ? document.referrer : '/';
  const pageUrl = isBrowser ? window.location.href : '/';

  return (
    <Query
      query={experimentsQuery}
      fetchPolicy="no-cache"
      variables={{ pageReferer, pageUrl }}
    >
      {({ data, loading }) => {
        const expList = (data && data.experiments) || [];
        return (
          <expContext.Provider
            value={{ experiments: expList, loading }}
            {...props}
          />
        );
      }}
    </Query>
  );
};

/**
 * Basic hook for experiments
 * @param {String} name
 * @param {Array} treatmentNames
 * @returns {{}|{treatment: {}, loading}}
 */
const useExperiment = ({ name, treatmentNames }) => {
  const context = useContext(expContext);
  const { experiments, loading } = context;
  const baseObj = { treatments: {}, loading };

  // if loading, we want to fire the loading component provide by the user
  if (loading) return baseObj;

  // If the service fails, or the experiments are undefined, we will want to show the control.
  if (!experiments) return baseObj;

  // match the name to the list of experiments that have come back
  const expObj = experiments.find(exp => name === exp.name);
  if (!expObj) return baseObj;

  const notAssigned = {};
  treatmentNames.forEach(treatment => {
    if (treatment !== expObj.treatment.name) {
      notAssigned[treatment] = {};
    }
  });

  const reduced = Object.keys(expObj).reduce(acc => {
    // eslint-disable-next-line no-param-reassign
    acc = {
      treatments: {
        [expObj.treatment.name]: {
          treatmentName: expObj.treatment.name,
          treatmentId: expObj.treatment.id,
          experimentId: expObj.id,
          experimentName: expObj.name,
          assignmentType: expObj.assignmentType,
        },
      },
      name: expObj.name,
      loading,
    };
    return acc;
  }, {});
  Object.assign(reduced.treatments, notAssigned);
  return reduced;
};

ExperimentProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { ExperimentProvider, useExperiment };
